/**
 * Position-preserving HTML scanner for the section editor.
 *
 * It does not build a DOM and never serialises anything: it only reports
 * where things are in the source string (tags, attribute values, text,
 * comments). The renderer splices new slot values into those offsets and
 * copies every other byte verbatim, which is what makes an unedited mail
 * come out byte-identical (comments, Outlook conditional comments,
 * attribute quoting and whitespace included). See
 * docs/section-editor-core.md for why this was chosen over htmlparser2.
 *
 * Deliberately tolerant: anything it does not understand stays plain
 * text. It is not a sanitiser and makes no safety decision; the saved
 * HTML still goes through `checkTemplateHtml`.
 */

export interface ScannedAttr {
  /** Lower-cased attribute name. */
  name: string;
  /** Offset of the first character of the name. */
  nameStart: number;
  /** Offsets of the value without its quotes; equal when there is no value. */
  valueStart: number;
  valueEnd: number;
  quote: '"' | "'" | '';
  hasValue: boolean;
}

export type Token =
  | { type: 'text'; start: number; end: number }
  /** A comment, doctype, CDATA or processing instruction: everything a browser does not render. */
  | { type: 'comment'; start: number; end: number }
  /** The content of script/style/title/textarea, never looked into. */
  | { type: 'raw'; start: number; end: number }
  | {
      type: 'open';
      start: number;
      end: number;
      name: string;
      attrs: ScannedAttr[];
      selfClosing: boolean;
    }
  | { type: 'close'; start: number; end: number; name: string };

export type OpenToken = Extract<Token, { type: 'open' }>;

export interface ScanResult {
  tokens: Token[];
  /** A comment or tag ran to the end of the input without being closed. */
  unterminated: boolean;
}

const RAW_TEXT_ELEMENTS = new Set([
  'script',
  'style',
  'textarea',
  'title',
  'xmp',
  'noembed',
  'noframes',
]);

function isLetter(code: number): boolean {
  return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
}

function isSpace(code: number): boolean {
  return code === 32 || code === 9 || code === 10 || code === 12 || code === 13;
}

/** Scans `html` between `from` and `to`. Offsets in the result are offsets into `html`. */
export function scan(html: string, from = 0, to = html.length): ScanResult {
  const tokens: Token[] = [];
  let unterminated = false;
  let textStart = from;
  let i = from;

  const flushText = (end: number): void => {
    if (end > textStart) tokens.push({ type: 'text', start: textStart, end });
  };

  while (i < to) {
    const lt = html.indexOf('<', i);
    if (lt === -1 || lt >= to) break;
    const next = html.charCodeAt(lt + 1);

    if (html.startsWith('<!--', lt)) {
      let end: number;
      if (html.startsWith('<!-->', lt)) end = lt + 5;
      else if (html.startsWith('<!--->', lt)) end = lt + 6;
      else {
        const close = html.indexOf('-->', lt + 4);
        if (close === -1 || close + 3 > to) {
          end = to;
          unterminated = true;
        } else end = close + 3;
      }
      flushText(lt);
      tokens.push({ type: 'comment', start: lt, end });
      i = textStart = end;
      continue;
    }

    if (next === 33 /* ! */ || next === 63 /* ? */) {
      const close = html.indexOf('>', lt + 2);
      const end = close === -1 || close >= to ? to : close + 1;
      if (close === -1 || close >= to) unterminated = true;
      flushText(lt);
      tokens.push({ type: 'comment', start: lt, end });
      i = textStart = end;
      continue;
    }

    if (next === 47 /* / */ && isLetter(html.charCodeAt(lt + 2))) {
      let p = lt + 2;
      while (p < to && !isSpace(html.charCodeAt(p)) && html[p] !== '>' && html[p] !== '/') p += 1;
      const name = html.slice(lt + 2, p).toLowerCase();
      const close = html.indexOf('>', p);
      const end = close === -1 || close >= to ? to : close + 1;
      if (close === -1 || close >= to) unterminated = true;
      flushText(lt);
      tokens.push({ type: 'close', start: lt, end, name });
      i = textStart = end;
      continue;
    }

    if (!isLetter(next)) {
      i = lt + 1;
      continue;
    }

    // Opening tag.
    let p = lt + 1;
    while (p < to && !isSpace(html.charCodeAt(p)) && html[p] !== '>' && html[p] !== '/') p += 1;
    const name = html.slice(lt + 1, p).toLowerCase();
    const attrs: ScannedAttr[] = [];
    let selfClosing = false;
    let closed = false;

    while (p < to) {
      const ch = html[p]!;
      if (ch === '>') {
        closed = true;
        p += 1;
        break;
      }
      if (ch === '/') {
        selfClosing = html[p + 1] === '>';
        p += 1;
        continue;
      }
      if (isSpace(html.charCodeAt(p))) {
        p += 1;
        continue;
      }
      const nameStart = p;
      p += 1; // a leading "=" belongs to the name, as in the HTML spec
      while (
        p < to &&
        !isSpace(html.charCodeAt(p)) &&
        html[p] !== '=' &&
        html[p] !== '>' &&
        html[p] !== '/'
      ) {
        p += 1;
      }
      const attrName = html.slice(nameStart, p).toLowerCase();
      let q = p;
      while (q < to && isSpace(html.charCodeAt(q))) q += 1;
      if (html[q] !== '=') {
        attrs.push({
          name: attrName,
          nameStart,
          valueStart: p,
          valueEnd: p,
          quote: '',
          hasValue: false,
        });
        continue;
      }
      q += 1;
      while (q < to && isSpace(html.charCodeAt(q))) q += 1;
      const quote = html[q];
      if (quote === '"' || quote === "'") {
        const endQuote = html.indexOf(quote, q + 1);
        const valueEnd = endQuote === -1 || endQuote >= to ? to : endQuote;
        attrs.push({
          name: attrName,
          nameStart,
          valueStart: q + 1,
          valueEnd,
          quote,
          hasValue: true,
        });
        p = Math.min(valueEnd + 1, to);
      } else {
        let v = q;
        while (v < to && !isSpace(html.charCodeAt(v)) && html[v] !== '>') v += 1;
        attrs.push({
          name: attrName,
          nameStart,
          valueStart: q,
          valueEnd: v,
          quote: '',
          hasValue: true,
        });
        p = v;
      }
    }

    if (!closed) unterminated = true;
    flushText(lt);
    tokens.push({ type: 'open', start: lt, end: p, name, attrs, selfClosing });
    i = textStart = p;

    if (closed && RAW_TEXT_ELEMENTS.has(name) && !selfClosing) {
      const closer = new RegExp(`</${name}(?=[\\s/>])`, 'gi');
      closer.lastIndex = p;
      const match = closer.exec(html);
      const rawEnd = match === null || match.index >= to ? to : match.index;
      if (rawEnd > p) tokens.push({ type: 'raw', start: p, end: rawEnd });
      i = textStart = rawEnd;
    }
  }

  flushText(to);
  return { tokens, unterminated };
}

/** The attribute called `name` (lower case) on an opening tag, if present. */
export function findAttr(token: OpenToken, name: string): ScannedAttr | undefined {
  return token.attrs.find((a) => a.name === name);
}
